import React, {
  ReactElement, Ref, useEffect, useRef, useState,
} from 'react';
import {
  View, StyleSheet, KeyboardAvoidingView, Text, ScrollView, TextInput, Platform, ActivityIndicator,
  Pressable,
} from 'react-native';
import { EdgeInsets, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useSelector } from 'react-redux';
import Modal from 'react-native-modal';
import { useNavigation } from '@react-navigation/native';
import AlertModal from '../../../../components/AlertModal';
import { TemplateUpdateOption } from '../../../../components/AssetManagement/AddTemplateModal/components/CreationOptionList/components/CreationOption';
import FieldsList from '../../../../components/AssetManagement/AddTemplateModal/components/FieldList';
import { ADDITIONAL_FIELD_TYPES } from '../../../../components/AssetManagement/AddTemplateModal/components/FieldList/components/FieldItem';
import IssueList, { customTemplateDefaultIssues } from '../../../../components/AssetManagement/AddTemplateModal/components/IssueList';
import MainSection from '../../../../components/AssetManagement/AddTemplateModal/components/MainSection';
import PresetTemplates from '../../../../components/AssetManagement/AddTemplateModal/components/PresetTemplates';
import TabList from '../../../../components/AssetManagement/AddTemplateModal/components/TabList';
import CustomTextField from '../../../../components/CustomTextField';
import isMobile from '../../../../components/IsMobile';
import nameRegex from '../../../../components/Regex';
import {
  TemplateGroup, Template, TemplateField, TemplateIssue, TemplateType, UserGroup, UserInstance,
  UserPermission,
} from '../../../../constants/Types';
import updateTemplate from '../../../../requests/Assets/UpdateTemplate';
import Style from '../../../../constants/Style';
import { logEvent } from '../../../../components/LogEvent';
import Button from '../../../../components/Button';
import Colors from '../../../../constants/Colors';
import BinIcon12 from '../../../../components/12pxIcons/BinIcon12';
import {
  DELETE_TEMPLATE_FAILURE, TEMPLATE_NAME_DUPLICATE_ERROR,
} from '../../../../constants/AlertMessages';
import deleteTemplate from '../../../../requests/Assets/DeleteTemplate';
import ResponsibleForPPM from './components/ResponsibleForPPM';
import IssueListPreview from '../../../../components/AssetManagement/AddTemplateModal/components/IssueList/components/IssueListPreview';
import { Building } from '../../../LocationManagement/components/AddBuildingModal';
import { BuildingSettingsPriority } from '../../../LocationManagement/components/BuildingSettingsModal/data';
import { DEFAULT_TEMPLATE_TITLE } from '../../../../constants/Utils';

// eslint-disable-next-line no-shadow, no-unused-vars
enum TemplateDetailsSection {
  // eslint-disable-next-line no-unused-vars
  MAIN = 'TEMPLATE DATA', FIELDS = 'FIELDS', ISSUES = 'ISSUES', PPM = 'PPM',
}

interface AddTemplateModalProps {
  hide: () => void,
  users: UserInstance[],
  userGroups: UserGroup[],
  templateGroups: TemplateGroup[],
  templateToUpdate?: Template,
  // eslint-disable-next-line no-unused-vars
  update: (template: Template, add: boolean, groupID: string) => void,
  showParentAlert,
  deleteAction,
  templateUpdateOption: TemplateUpdateOption,
  selectedTemplateId?: string,
  templates: Template[],
  selectedGroupId?: string,
}

const CreateTemplate = ({
  hide, templateToUpdate = null, templateGroups = [], update, deleteAction, showParentAlert,
  userGroups = [], users = [], templateUpdateOption, selectedTemplateId, templates, selectedGroupId,
}: AddTemplateModalProps) => {
  const insets: EdgeInsets = useSafeAreaInsets();
  const scrollView: Ref<ScrollView> = useRef<ScrollView>();
  const navigation = useNavigation();
  const [updatedTemplate, setUpdatedTemplate] = useState<Template>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [alertShown, setAlertShown] = useState<boolean>(false);
  const [alertDismissed, setAlertDismissed] = useState<boolean>(!alertShown);
  const [alertTitle, setAlertTitle] = useState<string>(null);
  const [alertMessage, setAlertMessage] = useState<string | ReactElement>(null);
  const [alertError, setAlertError] = useState<boolean>(true);
  const [alertAction, setAlertAction] = useState<() => void>(null);
  const [alertActionTitle, setAlertActionTitle] = useState<string>('');
  const [selectedTemplateName,
    setSelectedTemplateName] = useState<string>(templateToUpdate?.name ?? 'New template');
  const [templateTitle,
    setTemplateTitle] = useState<string>(templateToUpdate?.title ?? DEFAULT_TEMPLATE_TITLE);
  const [templateSubtitle,
    setTemplateSubtitle] = useState<string | null>(templateToUpdate?.subtitle ?? null);
  const [templateSubtitleUrl,
    setTemplateSubtitleUrl] = useState<string | null>(templateToUpdate?.subtitleUrl ?? null);
  const [sectionShown,
    setSectionShown] = useState<TemplateDetailsSection>(TemplateDetailsSection.MAIN);
  const [fields, setFields] = useState<TemplateField[]>([]);
  const [issues, setIssues] = useState<TemplateIssue[]>(customTemplateDefaultIssues());
  const [photoUploadEnabled,
    setPhotoUploadEnabled] = useState<boolean>(!templateToUpdate?.disablePhotoUpload);
  const [templateGroupDropdownShown, setTemplateGroupDropdownShown] = useState<boolean>(false);
  const [templateTypeDropdownShown, setTemplateTypeDropdownShown] = useState<boolean>(false);
  const [appclipPreviewShown, setAppclipPreviewShown] = useState<boolean>(false);
  const [focusedFieldIndex, setFocusedFieldIndex] = useState<number>(-1);
  const nameField: Ref<TextInput> = useRef<TextInput>();
  const fieldRefs = [];
  const [selectedTemplateGroupId,
    setSelectedTemplateGroupId] = useState<string | undefined>(undefined);
  const [selectedTemplateType,
    setSelectedTemplateType] = useState<TemplateType>(templateToUpdate?.type ?? TemplateType.ASSET);
  const [templateGroupNotSelected, setTemplateGroupNotSelected] = useState<boolean>(false);
  const [fieldTypesShownIndex, setFieldTypesShownIndex] = useState<number>(null);
  const [priorityDropdownShownIndex, setPriorityDropdownShownIndex] = useState<number | null>(-2);
  const [issueTagDropdownShownIndex, setIssueTagDropdownShownIndex] = useState<number | null>(-2);
  const [tagDropdownShownIndex, setTagDropdownShownIndex] = useState<number>(-1);
  const [categoryDropdownShownIndex, setCategoryDropdownShownIndex] = useState<number | null>(null);
  const slaNames: string[] = useSelector(
    (state: { selectedBuilding: Building }) => state.selectedBuilding.SlaHours
      ?.map((item: BuildingSettingsPriority) => item.Name),
  );
  const happyEnabled = useSelector((state: { happyEnabled: { enabled: boolean } }) => state
    .happyEnabled.enabled);
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const canEditTemplates: boolean = isAdmin
    || permissions?.includes(UserPermission.TEMPLATES_UPDATE);
  const canDeleteTemplates: boolean = isAdmin
    || permissions?.includes(UserPermission.TEMPLATES_DELETE);
  const [ppmLink, setPpmLink] = useState<string>(templateToUpdate?.ppmUrl ?? '');
  const [ppmLinkError, setPpmLinkError] = useState<string | null>(null);
  const [subtitleUrlInvalid, setSubtitleUrlInvalid] = useState<boolean>(false);
  const mainShown: boolean = sectionShown === TemplateDetailsSection.MAIN;
  const fieldsShown: boolean = sectionShown === TemplateDetailsSection.FIELDS;
  const issuesShown: boolean = sectionShown === TemplateDetailsSection.ISSUES;
  const ppmShown: boolean = sectionShown === TemplateDetailsSection.PPM;
  const ppmEnabled: boolean = true;
  const ppmResponsibilityEnabled: boolean = false;
  const templateNameEmpty: boolean = !(selectedTemplateName?.length > 0);
  const templateNameDuplicate: boolean = templates
    .some((template: Template) => template.name.trim().toLowerCase()
      === selectedTemplateName.trim().toLowerCase()
      && template.id !== templateToUpdate?.id);
  const templateNameInvalid: boolean = templateNameEmpty || templateNameDuplicate;
  const linkRegex: RegExp = new RegExp(ADDITIONAL_FIELD_TYPES.LINK);

  const templateErrorMessage: () => string = () => {
    if (templateNameEmpty) return 'Template name cannot be empty';
    if (templateNameDuplicate) return TEMPLATE_NAME_DUPLICATE_ERROR;
    return '';
  };

  useEffect(() => {
    const unsubscribe = navigation.addListener('blur', closeDropdowns);
    return unsubscribe;
  }, []);

  useEffect(() => setTemplateGroupNotSelected(false), [selectedTemplateGroupId]);

  useEffect(() => setSubtitleUrlInvalid(false), [templateSubtitleUrl]);

  const showError = (errorText?: string) => showAlert(
    'Error',
    errorText
    ?? `We are unable to ${templateToUpdate?.id ? 'edit' : 'add'} the template right now. Please try again later`,
  );

  const attemptUpdate: () => void = () => {
    setLoading(true);
    let issuesToUpdate: TemplateIssue[] = [...issues];
    if (templateUpdateOption !== TemplateUpdateOption.EDIT) {
      issuesToUpdate = issuesToUpdate?.map((issue: TemplateIssue) => ({
        Text: issue.Text,
        UserGroupId: issue.UserGroupId ?? [],
        Priority: issue.Priority,
        Enabled: issue.Enabled ?? true,
        Category: issue.Category,
      }));
    } else {
      issuesToUpdate = issuesToUpdate?.map((issue: TemplateIssue) => {
        const outdatedIssue: TemplateIssue = templateToUpdate?.issues
          ?.filter((originalIssue: TemplateIssue) => originalIssue?.Id === issue?.Id)[0];
        const updatedIssue: TemplateIssue = {
          Id: issue.Id,
          Text: issue.Text,
          UserGroupId: issue.UserGroupId ?? [],
          Enabled: issue.Enabled ?? true,
          Priority: issue.Priority,
          Category: issue.Category,
        };
        if (outdatedIssue?.UserGroupId?.length > 0) {
          updatedIssue.PrevUserGroupId = [];
          outdatedIssue.UserGroupId.forEach((groupId: string) => {
            if (!updatedIssue.UserGroupId.includes(groupId)) {
              updatedIssue.PrevUserGroupId.push(groupId);
            }
          });
        }
        return updatedIssue;
      });
    }
    const templateGroupName: string = templateGroups?.filter((group: TemplateGroup) => group
      .ButtonPrototypeGroupId === selectedTemplateGroupId)[0]?.Name ?? '';
    let ppmLinkWithoutId = ppmLink;
    if (ppmLink && !ppmLink?.trim().endsWith('=')) {
      ppmLinkWithoutId = `${ppmLink?.slice(0, ppmLink.lastIndexOf('='))}=`;
    }
    updateTemplate({
      Name: selectedTemplateName.trim(),
      GroupId: selectedTemplateGroupId,
      GroupName: templateGroupName.trim(),
      Fields: fields,
      Issues: issuesToUpdate,
      ButtonPrototypeId: templateToUpdate?.id,
      Type: selectedTemplateType ?? TemplateType.ASSET,
      PpmUrl: ppmLinkWithoutId?.trim(),
      Title: (templateTitle ?? '').trim() || DEFAULT_TEMPLATE_TITLE,
      Subtitle: (templateSubtitle ?? '').trim(),
      SubtitleUrl: (templateSubtitleUrl ?? '').trim(),
      disablePhotoUpload: !photoUploadEnabled,
    }).then((response) => {
      if (response && !response.isError) {
        const responseTemplate = response.ButtonPrototype
          ?? JSON.parse(response.msg.body).ButtonPrototype;
        const savedTemplate: Template = {
          name: responseTemplate.Name,
          id: responseTemplate.ButtonPrototypeId,
          issues: responseTemplate.Issues,
          customfields: responseTemplate.CustomFields,
          type: responseTemplate.Type,
          ppmUrl: responseTemplate.PpmUrl,
          title: responseTemplate.Title,
          subtitle: responseTemplate.Subtitle,
          disablePhotoUpload: responseTemplate.DisablePhotoUpload ?? !photoUploadEnabled,
        };
        setUpdatedTemplate(savedTemplate);
        if (Platform.OS === 'ios') {
          showAlert(
            `The template was successfully ${templateToUpdate?.id ? 'edited' : 'added'}`,
            <Text>
              {'Template '}
              <Text style={Style.alertBoldText}>
                {selectedTemplateName?.trim()}
              </Text>
              {` successfully ${templateToUpdate?.id ? 'edited' : 'added'}`}
              {!templateToUpdate?.id && templateGroupName?.length
                && <Text>
                  {' to template group '}
                  <Text style={Style.alertBoldText}>{templateGroupName}</Text>
                </Text>}
            </Text>,
            false,
          );
        } else {
          update(savedTemplate, !templateToUpdate?.id, selectedTemplateGroupId);
          hide();
          showParentAlert(
            `Template ${templateToUpdate?.id ? 'edited' : 'added'} successfully`,
            <Text>
              {'Template '}
              <Text style={Style.alertBoldText}>
                {selectedTemplateName?.trim()}
              </Text>
              {` successfully ${templateToUpdate?.id ? 'edited' : 'added'}`}
              {!templateToUpdate?.id && templateGroupName?.length
                && <Text>
                  {' to template group '}
                  <Text style={Style.alertBoldText}>{templateGroupName}</Text>
                </Text>}
            </Text>,
            false,
          );
        }
      } else showError(response?.errorText);
      setLoading(false);
    }).catch((error) => showError(error?.message));
  };

  const onAlertDismiss: () => void = () => {
    setAlertDismissed(true);
    if (Platform.OS === 'ios' && updatedTemplate) {
      hide();
      update(updatedTemplate, !templateToUpdate?.id, selectedTemplateGroupId);
    }
  };

  const closeDropdowns: () => void = () => {
    setTemplateGroupDropdownShown(false);
    setTemplateTypeDropdownShown(false);
    setFieldTypesShownIndex(null);
    setPriorityDropdownShownIndex(null);
    setIssueTagDropdownShownIndex(null);
    setTagDropdownShownIndex(null);
    setCategoryDropdownShownIndex(null);
  };

  const validate = (): boolean => {
    const errorMessages: string[] = [];
    if (!selectedTemplateName?.trim().length) {
      setFocusedFieldIndex(0);
      errorMessages.push('- Template name cannot be empty');
      setSectionShown(TemplateDetailsSection.MAIN);
    }
    if (!nameRegex.exp.test(selectedTemplateName?.trim())) {
      if (!errorMessages.length) {
        setFocusedFieldIndex(0);
        setSectionShown(TemplateDetailsSection.MAIN);
      }
      errorMessages.push(`- Template name '${selectedTemplateName?.trim()}' invalid;`);
      errorMessages.push(`- Template name${nameRegex.message}`);
    }
    if (templateNameDuplicate) {
      if (!errorMessages.length) {
        setFocusedFieldIndex(0);
        setSectionShown(TemplateDetailsSection.MAIN);
      }
      errorMessages.push(`- ${TEMPLATE_NAME_DUPLICATE_ERROR}`);
    }
    if (!selectedTemplateGroupId) {
      setTemplateGroupNotSelected(true);
      if (!errorMessages.length) {
        setFocusedFieldIndex(1);
        setSectionShown(TemplateDetailsSection.MAIN);
      }
      errorMessages.push('- A template group must be chosen to add the template to');
    }
    if (templateSubtitleUrl?.trim() && !linkRegex.test(templateSubtitleUrl.trim())) {
      setSectionShown(TemplateDetailsSection.MAIN);
      setSubtitleUrlInvalid(true);
      errorMessages.push(`- Template subtitle URL '${templateSubtitleUrl?.trim()}' invalid`);
    }
    fields?.forEach((field: TemplateField, index: number) => {
      if (!(field.Name?.trim().length > 0)) {
        if (!errorMessages.length) {
          setFocusedFieldIndex(index + 2);
          setSectionShown(TemplateDetailsSection.FIELDS);
        }
        errorMessages.push('- Additional field names cannot be empty');
        return;
      }
      if (!nameRegex.exp.test(field.Name?.trim())) {
        if (!errorMessages.length) {
          setFocusedFieldIndex(index + 2);
          setSectionShown(TemplateDetailsSection.FIELDS);
        }
        errorMessages.push(`- Additional field name '${field.Name?.trim()}' invalid`);
        errorMessages.push(`- Additional field names${nameRegex.message}`);
      }
      fields.slice(0, index)?.forEach((previousField: TemplateField) => {
        if (previousField.Name.trim().toLowerCase() === field.Name.trim().toLowerCase()) {
          if (!errorMessages.length) {
            setFocusedFieldIndex(index + 2);
            setSectionShown(TemplateDetailsSection.FIELDS);
          }
          errorMessages
            .push(`- Field name '${field.Name}' is a duplicate. Field names must be unique within the template`);
        }
      });
    });
    if (issues?.length > 1) {
      let enabledIssuesExist: boolean = false;
      issues.forEach((issue: TemplateIssue, index: number) => {
        if (issue.Enabled) {
          enabledIssuesExist = true;
          if (!issue.Text?.trim().length) {
            if (!errorMessages.length) setSectionShown(TemplateDetailsSection.ISSUES);
            errorMessages.push('- Issue title cannot be empty');
            return;
          }
          if (!nameRegex.exp.test(issue.Text?.trim())) {
            if (!errorMessages.length) setSectionShown(TemplateDetailsSection.ISSUES);
            errorMessages.push(`- Issue name '${issue.Text?.trim()}' invalid`);
            errorMessages.push(`- Issue titles${nameRegex.message}`);
          } else {
            issues.slice(0, index)?.forEach((previousIssue: TemplateIssue) => {
              if (previousIssue.Text.trim().toLowerCase() === issue.Text.trim().toLowerCase()) {
                if (!errorMessages.length) setSectionShown(TemplateDetailsSection.ISSUES);
                errorMessages
                  .push(`- Issue name '${issue.Text}' is a duplicate. Issue names must be unique within the template`);
              }
            });
          }
          const issueUserGroups: string[] = issue.UserGroupId?.filter((id: string) => userGroups
            .map((userGroup: UserGroup) => userGroup.UserGroupId).includes(id));
          if (!issueUserGroups?.length) {
            if (!errorMessages.length) setSectionShown(TemplateDetailsSection.ISSUES);
            errorMessages.push(`- Issue '${issue.Text?.trim()}' is not assigned to any user group`);
          }
          if (![0, 1, 2, 3, 4].includes(issue.Priority)) {
            if (!errorMessages.length) setSectionShown(TemplateDetailsSection.ISSUES);
            errorMessages
              .push(`- Issue '${issue.Text?.trim()}' does not have a priority level specified`);
          }
        }
      });
      if (!enabledIssuesExist) {
        if (!errorMessages.length) setSectionShown(TemplateDetailsSection.ISSUES);
        errorMessages.push('- At least one issue must be enabled');
      }
    } else {
      if (!errorMessages.length) setSectionShown(TemplateDetailsSection.ISSUES);
      errorMessages.push('- A template should have at least one issue');
    }
    if (ppmEnabled && ppmLink?.trim().length > 0 && !linkRegex.test(ppmLink?.trim())) {
      if (!errorMessages.length) setSectionShown(TemplateDetailsSection.PPM);
      setPpmLinkError('Value is not a valid hyperlink');
      errorMessages.push('- PPM link provided is not a valid hyperlink');
    } else setPpmLinkError(null);
    if (errorMessages.length) showAlert('Error', Array.from(new Set(errorMessages)).join('\n'));
    return !errorMessages.length;
  };

  const getIssueData = (issueList: TemplateIssue[]) => {
    if (!issueList) return null;
    const newIssueList: TemplateIssue[] = issueList
      .filter((issue) => issue.Text?.length > 0 && issue.Text.toLowerCase() !== 'other')
      .map((issue: any) => ({
        UserGroupId: [...issue.UserGroupId ?? []],
        Text: issue.Text,
        Priority: issue.Priority,
        Id: issue.Id,
        Enabled: issue.Enabled ?? true,
        Category: issue.Category,
      }));
    const otherIssue: TemplateIssue = issueList
      .filter((issue) => issue.Text?.length > 0 && issue.Text.toLowerCase() === 'other')[0];
    if (otherIssue) {
      newIssueList.push({
        UserGroupId: [...otherIssue.UserGroupId ?? []],
        Text: otherIssue.Text,
        Priority: otherIssue.Priority,
        Id: otherIssue.Id,
        Enabled: otherIssue.Enabled ?? true,
      });
    }
    return newIssueList;
  };

  const setDefaultIssues: () => void = () => {
    if (templateToUpdate?.issues) setIssues(getIssueData(templateToUpdate.issues));
    else {
      const selectedTemplate: Template = (templateUpdateOption === TemplateUpdateOption.DEFAULT
        ? PresetTemplates : templates)
        ?.filter((template: Template) => template.id === selectedTemplateId)[0];
      setIssues(getIssueData(selectedTemplate?.issues) ?? customTemplateDefaultIssues());
    }
  };

  useEffect(() => {
    if (alertShown) setAlertDismissed(false);
  }, [alertShown]);

  const focusField: () => void = () => {
    if (issuesShown) {
      setTemplateGroupDropdownShown(false);
      setTemplateTypeDropdownShown(false);
    }
    let count: number = 0;
    let fieldIndex: number = focusedFieldIndex;
    if (count < 5 && fieldIndex >= 0 && sectionShown !== TemplateDetailsSection.ISSUES
      && !alertShown
      && ((focusedFieldIndex === 0 && nameField?.current) || focusedFieldIndex > 1)) {
      count += 1;
      if (focusedFieldIndex === 0) nameField?.current?.focus();
      else if (focusedFieldIndex > 1) fieldRefs[focusedFieldIndex - 2]?.focus();
      fieldIndex = -1;
      if (!(focusedFieldIndex > 1)) setFocusedFieldIndex(-1);
    }
  };

  useEffect(focusField, [
    sectionShown, focusedFieldIndex, nameField?.current, alertDismissed,
    fieldRefs[focusedFieldIndex],
  ]);

  useEffect(closeDropdowns, [sectionShown]);

  useEffect(() => setPpmLinkError(null), [ppmLink]);

  useEffect(() => {
    if (fieldTypesShownIndex !== null) {
      setTemplateGroupDropdownShown(false);
      setTemplateTypeDropdownShown(false);
    }
  }, [fieldTypesShownIndex]);

  useEffect(() => {
    const selectedTemplate: Template | null = templateToUpdate
      ?? (templateUpdateOption === TemplateUpdateOption.DEFAULT ? PresetTemplates : templates)
        ?.filter((template: Template) => template.id === selectedTemplateId)[0];
    setSectionShown(TemplateDetailsSection.MAIN);
    setSelectedTemplateType(selectedTemplate?.type ?? TemplateType.ASSET);
    setSelectedTemplateName(selectedTemplate?.name ?? 'New template');
    setTemplateTitle(selectedTemplate?.title ?? DEFAULT_TEMPLATE_TITLE);
    setTemplateSubtitle(selectedTemplate?.subtitle ?? '');
    setTemplateSubtitleUrl(selectedTemplate?.subtitleUrl ?? '');
    setFields([...(selectedTemplate?.customfields ?? [])
      ?.map((field: TemplateField) => ({ ...field })) ?? []]);
    setDefaultIssues();
    setSelectedTemplateGroupId(selectedGroupId
      ?? templateGroups?.filter((group: TemplateGroup) => group.buttonPrototypes
        ?.map((item: Template) => item.id)?.includes(templateToUpdate?.id))[0]
        ?.ButtonPrototypeGroupId);
    setPhotoUploadEnabled(!selectedTemplate?.disablePhotoUpload);
  }, [templateUpdateOption, templateToUpdate]);

  const showAlert = (
    title: string,
    message: string | ReactElement,
    error: boolean = true,
    action: () => void = null,
    actionTitle: string = '',
  ) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertError(error);
    setAlertAction(() => action);
    setAlertActionTitle(actionTitle);
    setLoading(false);
    setAlertShown(true);
  };

  const scrollToEnd: () => void = () => {
    const timeout = setTimeout(() => {
      scrollView.current?.scrollToEnd();
      clearTimeout(timeout);
    }, 200);
  };

  const editName: () => void = () => {
    logEvent('AddTemplateModal', 'edit_icon');
    setSectionShown(TemplateDetailsSection.MAIN);
    setFocusedFieldIndex(0);
  };

  const editTitle: () => void = () => {
    logEvent('AddTemplateModal', 'edit_icon');
    setSectionShown(TemplateDetailsSection.MAIN);
  };

  const deleteError = (text?: string) => showAlert(
    'Error',
    text ?? DELETE_TEMPLATE_FAILURE,
  );

  const attemptDeletion: () => void = () => {
    setLoading(true);
    deleteTemplate(templateToUpdate.id).then((res: any) => {
      if (res && !res.isError) {
        showParentAlert(
          'Template successfully deleted',
          <Text>
            <Text style={Style.alertBoldText}>{templateToUpdate.name}</Text>
            {' was successfully deleted'}
          </Text>,
          false,
        );
        deleteAction(templateToUpdate.id);
        hide();
      } else deleteError(res?.errorText);
    }).catch((error) => deleteError(error?.message))
      .finally(() => setLoading(false));
  };

  const promptDeletion = () => {
    logEvent('TemplateGroupRowMobile', 'Delete');
    showAlert(
      'Delete template',
      <>
        {'Are you sure you want to delete the template '}
        <Text style={Style.alertBoldText}>{templateToUpdate.name}</Text>
      </>,
      true,
      attemptDeletion,
      'Delete',
    );
  };

  const content = () => <View style={{ marginTop: 24, flex: 1 }}>
    <View style={Style.row}>
      <Text style={styles.title}>
        {templateToUpdate ? templateToUpdate.name : 'Create Template'}
      </Text>
      {templateToUpdate && canDeleteTemplates && <Button
        style={styles.deleteButton}
        children={<BinIcon12 color={styles.deleteButton.borderColor} />}
        onPress={promptDeletion}
      />}
      <Button
        onPress={hide}
        title={(canEditTemplates || canDeleteTemplates) ? 'Cancel' : 'Back'}
        testID='close-create-template-modal'
      />
      {canEditTemplates && <Button
        onPress={() => {
          if (validate()) attemptUpdate();
        }}
        affirmative
        title='Save'
        testID='close-create-template-modal'
        style={styles.saveButton}
      />}
    </View>
    <View style={[
      styles.container,
      { paddingBottom: styles.container.paddingBottom + insets.bottom },
    ]}>
      <View style={styles.header}>
        <Text style={[styles.headerTitle, Platform.OS === 'ios' && { marginTop: insets.top - 12 }]}>
          {templateUpdateOption === TemplateUpdateOption.EDIT ? 'Edit ' : 'Add '}
          {mainShown && 'template'}
          {fieldsShown && 'fields'}
          {issuesShown && 'issues'}
          {ppmShown && 'PPM information'}
        </Text>
        <TabList
          {...{ ppmEnabled, sectionShown, setSectionShown }}
          type={TemplateDetailsSection}
        />
      </View>
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <ScrollView
          contentContainerStyle={styles.scrollViewContent}
          nestedScrollEnabled
          ref={scrollView}
          scrollEventThrottle={16}
        >
          {mainShown && <View style={styles.section}>
            <MainSection
              {...{
                templateGroupDropdownShown,
                setTemplateGroupDropdownShown,
                templateTypeDropdownShown,
                setTemplateTypeDropdownShown,
                templateGroupNotSelected,
                templateGroups,
                templates,
                selectedTemplateGroupId,
                setFieldTypesShownIndex,
                selectedTemplateName,
                setSelectedTemplateName,
                closeDropdowns,
                nameField,
                setSelectedTemplateGroupId,
                selectedTemplateType,
                setSelectedTemplateType,
                happyEnabled,
                templateTitle,
                setTemplateTitle,
                templateSubtitle,
                setTemplateSubtitle,
                templateSubtitleUrl,
                setTemplateSubtitleUrl,
                subtitleUrlInvalid,
                templateNameInvalid,
                templateErrorMessage,
              }}
              currentTemplate={templateToUpdate}
            />
          </View>}
          {fieldsShown && <View style={styles.section}>
            <FieldsList
              {...{
                fields,
                setFields,
                fieldRefs,
                fieldTypesShownIndex,
                setFieldTypesShownIndex,
                scrollToEnd,
              }}
            />
          </View>}
          {issuesShown && <View>
            <View style={styles.section}>
              <View style={Style.row}>
                <View>
                  <Text style={styles.subTitle}>{'Issues Preview'}</Text>
                  <Text style={styles.label}>
                    {'See what Appclip will look like in a live preview'}
                  </Text>
                </View>
                <Button
                  affirmative
                  title='Live preview'
                  style={styles.button}
                  onPress={() => setAppclipPreviewShown(true)}
                />
              </View>
            </View>
            <View style={styles.section}>
              <IssueList
                {...{
                  setIssues,
                  issues,
                  userGroups,
                  tagDropdownShownIndex,
                  priorityDropdownShownIndex,
                  issueTagDropdownShownIndex,
                  setTagDropdownShownIndex,
                  setPriorityDropdownShownIndex,
                  setIssueTagDropdownShownIndex,
                  categoryDropdownShownIndex,
                  setCategoryDropdownShownIndex,
                  scrollToEnd,
                  slaNames,
                  selectedTemplateName,
                  editName,
                  editTitle,
                  photoUploadEnabled,
                  setPhotoUploadEnabled,
                }}
              />
            </View>
          </View>}
          {ppmShown && <View style={{ flexDirection: 'row' }}>
            <View style={styles.section}>
              <Text style={styles.subTitle}>{'Add PPM'}</Text>
              <CustomTextField
                title='PPM link'
                placeholder='Enter PPM link'
                value={ppmLink}
                updateValue={setPpmLink}
                invalid={!!ppmLinkError}
                errorText={ppmLinkError}
                editable={canEditTemplates}
              />
            </View>
            {ppmResponsibilityEnabled && <View style={styles.section}>
              <ResponsibleForPPM {...{ userGroups, users }} />
            </View>}
          </View>}
        </ScrollView>
      </KeyboardAvoidingView>
      <ActivityIndicator
        size='large'
        animating={loading}
        style={Style.activityIndicator}
      />
    </View>
    <Modal
      isVisible={appclipPreviewShown}
      onBackdropPress={() => setAppclipPreviewShown(false)}
      onBackButtonPress={() => setAppclipPreviewShown(false)}
      style={Style.modal}
    >
      <IssueListPreview
        {...{
          templateSubtitle,
          templateSubtitleUrl,
          templateTitle,
          issues,
          editTitle,
        }}
        hide={() => setAppclipPreviewShown(false)}
      />
    </Modal>
  </View>;

  return <>
    {isMobile ? content() : <Pressable
      onPress={closeDropdowns}
      children={content()}
      style={{ flex: 1, cursor: 'auto' }}
    />}
    <AlertModal
      onModalHide={onAlertDismiss}
      modalVisible={alertShown}
      setModalVisible={setAlertShown}
      title={alertTitle}
      message={alertMessage}
      error={alertError}
      action={alertAction}
      actionTitle={alertActionTitle}
    />
  </>;
};

const styles = StyleSheet.create({
  container: {
    paddingBottom: isMobile ? 16 : 40,
    marginVertical: isMobile ? 16 : 20,
    paddingTop: isMobile ? 60 : undefined,
    borderRadius: isMobile ? 0 : 10,
    backgroundColor: '#FFF',
    alignSelf: 'stretch',
    flex: 1,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue50,
  },
  header: {
    alignSelf: 'stretch',
    ...Style.row,
    margin: 16,
    marginBottom: 24,
  },
  scrollViewContent: {
    paddingHorizontal: isMobile ? 16 : 0,
    paddingVertical: isMobile ? 12 : 0,
  },
  headerTitle: {
    fontSize: 20,
    lineHeight: 30,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
    flex: 1,
  },
  title: {
    fontSize: 22,
    lineHeight: 33,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
    flex: 1,
  },
  subTitle: {
    fontSize: 18,
    lineHeight: 27,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack600,
  },
  deleteButton: {
    alignSelf: 'stretch',
    marginEnd: 8,
    borderColor: Colors.light.hereworksRed700,
  },
  section: {
    marginHorizontal: 16,
    marginBottom: 16,
    padding: 16,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue50,
    borderRadius: 16,
    width: 526,
  },
  label: {
    fontSize: 16,
    lineHeight: Platform.OS === 'ios' ? undefined : 24,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
    marginBottom: 4,
  },
  button: {
    width: 136,
  },
  saveButton: {
    marginStart: 8,
  },
});

export default CreateTemplate;
